import React, { useRef } from "react";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Chip, CircularProgress } from "@material-ui/core";
import { primary, darkBlue, red } from "../../styles/colors";
import { formatDate } from "../DuePayments/duePaymentsService";
import { useLoadMoreObserver } from "./useLoadMoreObserver";

const HEADERS = [
  { key: "brand", label: "Brand" },
  { key: "packaging", label: "Packaging" },
  { key: "bag_invoice_number", label: "Invoice No." },
  { key: "date", label: "Date", align: "center" },
  { key: "order_quantity", label: "Order Qty", align: "right" },
  { key: "supplier", label: "Supplier" },
  { key: "dispatched_quantity", label: "Dispatched Qty", align: "right" },
  { key: "balance_quantity", label: "Balance Qty", align: "right" },
  { key: "status", label: "Status", align: "center" },
];

const statusColors = {
  "Partially Dispatched": { bg: "#fff3cd", color: "#856404" },
  "Fully Dispatched": { bg: "#d4edda", color: "#155724" },
  Pending: { bg: "#e8e8e8", color: "#495057" },
};

const formatQty = (value) =>
  value === null || value === undefined ? "—" : Number(value).toLocaleString("en-IN");

const BagsTable = ({ rows = [], hasMore, loadingMore, onLoadMore }) => {
  const containerRef = useRef(null);
  const loadMoreTriggerRef = useLoadMoreObserver(containerRef, hasMore, loadingMore, onLoadMore);

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      ref={containerRef}
      style={{
        borderRadius: "8px",
        border: "1px solid #e9ecef",
        maxHeight: "calc(90vh - 260px)",
        overflow: "auto",
      }}
    >
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow style={{ backgroundColor: "#f8f9fa" }}>
            {HEADERS.map((h) => (
              <TableCell
                key={h.key}
                align={h.align || "left"}
                style={{
                  fontWeight: 600,
                  fontSize: "12px",
                  color: "#495057",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  padding: "12px",
                  borderBottom: "2px solid #e9ecef",
                  whiteSpace: "nowrap",
                }}
              >
                {h.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={HEADERS.length} align="center" style={{ padding: "40px", color: "#6c757d" }}>
                No bag purchase orders found
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row) => {
              const statusLabel = row.status || "Pending";
              const statusConfig = statusColors[statusLabel] || { bg: "#e8e8e8", color: "#495057" };
              const balanceNegative = Number(row.balance_quantity) < 0;
              return (
                <TableRow key={row.id} hover>
                  <TableCell style={{ padding: "10px 12px", fontWeight: 500, color: darkBlue }}>
                    {row.brand || "—"}
                  </TableCell>
                  <TableCell style={{ padding: "10px 12px" }}>{row.packaging || "—"}</TableCell>
                  <TableCell style={{ padding: "10px 12px" }}>{row.bag_invoice_number || "—"}</TableCell>
                  <TableCell align="center" style={{ padding: "10px 12px" }}>
                    {formatDate(row.date)}
                  </TableCell>
                  <TableCell align="right" style={{ padding: "10px 12px" }}>
                    {formatQty(row.order_quantity)}
                  </TableCell>
                  <TableCell style={{ padding: "10px 12px" }}>{row.supplier || "—"}</TableCell>
                  <TableCell align="right" style={{ padding: "10px 12px" }}>
                    {formatQty(row.dispatched_quantity)}
                  </TableCell>
                  <TableCell
                    align="right"
                    style={{ padding: "10px 12px", fontWeight: 600, color: balanceNegative ? red : darkBlue }}
                  >
                    {formatQty(row.balance_quantity)}
                  </TableCell>
                  <TableCell align="center" style={{ padding: "10px 12px" }}>
                    <Chip
                      label={statusLabel}
                      size="small"
                      style={{
                        backgroundColor: statusConfig.bg,
                        color: statusConfig.color,
                        fontSize: "0.7rem",
                        fontWeight: 600,
                        height: "22px",
                      }}
                    />
                  </TableCell>
                </TableRow>
              );
            })
          )}
          {hasMore && (
            <TableRow>
              <TableCell colSpan={HEADERS.length} align="center" style={{ padding: "16px" }}>
                <div
                  ref={loadMoreTriggerRef}
                  style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "8px", color: "#6c757d" }}
                >
                  {loadingMore && <CircularProgress size={18} style={{ color: primary }} />}
                  <span style={{ fontSize: "0.8rem" }}>{loadingMore ? "Loading more..." : "Scroll for more"}</span>
                </div>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default BagsTable;
